import { BaseCard } from "./common/BaseCard";
import { MatchScore } from "./MatchScore";
import { cn } from "@/lib/utils";
import { useNavigate } from "react-router-dom";
import { formatDate } from "@/utils/dateFormatter";
import { useTranslation } from "react-i18next";
import { useKanbanColumns } from "@/hooks/useKanbanColumns";

export interface Match {
  id: string;
  candidate_id: string | null;
  job_id: string | null;
  score: number;
  description: string | null;
  strengths: string[] | null;
  weaknesses: string[] | null;
  status: string | null;
  ai_analysis: any | null;
  created_at: string | null;
  updated_at: string | null;
  // Legacy fields for backwards compatibility
  candidateName?: string;
  candidateId?: string;
  jobId?: string;
  jobTitle?: string;
  companyId?: string;
  companyName?: string;
  createdAt?: string;
}

interface MatchCardProps {
  match: Match;
  className?: string;
  disableLinks?: boolean;
  isSelected?: boolean;
}

export const MatchCard = ({
  match,
  className,
  disableLinks = false,
  isSelected = false
}: MatchCardProps) => {
  const { t } = useTranslation();
  const { getStatusLabel } = useKanbanColumns();
  const navigate = useNavigate();
  
  const matchUrl = `/matches/${match.id}`;

  // Handle link clicks - only intercept regular clicks for React Router navigation
  // For modifier clicks (ctrl/cmd), let the browser handle it naturally via the href
  const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    // For modifier key clicks, let browser handle native <a> behavior
    if (e.ctrlKey || e.metaKey || e.shiftKey || e.button !== 0)
      return;
    
    // For regular clicks, use React Router
    e.preventDefault();
    navigate(matchUrl);
  };

  // Handle card click - only for regular clicks
  const handleCardClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (disableLinks) return;
    // Don't navigate if clicking on links or buttons
    if ((e.target as HTMLElement).closest('a, button')) return;
    // For modifier key clicks, open in new tab
    if (e.ctrlKey || e.metaKey || e.shiftKey || e.button !== 0) {
      window.open(matchUrl, '_blank', 'noopener');
      return;
    }
    navigate(matchUrl);
  };

  const headerActions = <MatchScore score={match.score} size="lg" showText={true} />;
  const footerContent = <div className="flex justify-end items-end p-4 pt-2">
    {disableLinks ? (
      <div className={cn(
        "inline-flex items-center justify-center px-3 py-1 text-sm font-medium rounded-sm bg-blue-500 text-white cursor-default",
      )}>
        {t("contacts.view")}
      </div>
    ) : (
      <a
        href={matchUrl}
        onClick={handleLinkClick}
        className={cn(
          "inline-flex items-center justify-center px-3 py-1 text-sm font-medium rounded-sm bg-blue-500 text-white hover:bg-blue-500 transition-colors dark:bg-blue-500 dark:hover:bg-blue-500",
        )}
      >
        {t("contacts.view")}
      </a>
    )}
  </div>;

  return <div
    onClick={handleCardClick}
    style={{ cursor: disableLinks ? 'default' : 'pointer' }}
    data-tour="match-card"
  >
    <BaseCard 
      id={match.id} 
      title={match.candidateName} 
      className={cn(
        "overflow-hidden transition-all duration-300 hover:shadow-lg backdrop-blur-lg bg-white/80 dark:bg-black/60 border border-white/30 dark:border-white/20 rounded-sm",
        "hover:bg-white/90 dark:hover:bg-black/70 hover:scale-[1.02] hover:border-white/40 dark:hover:border-white/30",
        isSelected && "ring-2 ring-blue-500 border-blue-500",
        "pt-4 pr-4",
        className
      )} 
      headerActions={headerActions} 
      footerContent={footerContent}
    >
      <div className="space-y-4">
        <div className="space-y-2">
          {disableLinks ? (
            <div className="cursor-default">
              <h3 className="text-lg font-semibold truncate mb-1 text-gray-900 dark:text-gray-100">
                {match.jobTitle}
              </h3>
            </div>
          ) : (
            <a 
              href={matchUrl}
              onClick={handleLinkClick}
              className="hover:text-primary transition-colors"
            >
              <h3 className="text-lg font-semibold truncate mb-1 text-gray-900 dark:text-gray-100">
                {match.jobTitle}
              </h3>
            </a>
          )}
          <span className="text-sm text-gray-800 block dark:text-gray-200">
            {match.companyName}
          </span>
        </div>
        <div className="flex items-center justify-between text-sm">
          <span className="px-3 py-1 rounded-sm bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300">
            {match.status ? getStatusLabel(match.status) : t('matching.statusLabels.matched')}
          </span>
          <span className="text-gray-600 dark:text-gray-400">
            {formatDate(match.createdAt)}
          </span>
        </div>
        <p className="text-gray-600 dark:text-gray-300">{match.description}</p>
        {match.strengths && match.strengths.length > 0 && <div>
          <h4 className="text-sm font-medium mb-2 text-gray-900 dark:text-gray-100">Key strengths:</h4>
          <ul className="list-disc pl-5 text-sm space-y-1 text-gray-600 dark:text-gray-300">
            {match.strengths.map((strength, index) => <li key={index}>{strength}</li>)}
          </ul>
        </div>}
      </div>
    </BaseCard>
  </div>;
};
